import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api, isApiError } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'
import { Avatar, PeoplePicker } from './PeoplePicker'
import { IconClose, IconPlus, IconSearch } from './NavIcons'
import { BID_ROLES, PROFILES, type BidMember, type BidRole, type Profile } from '../lib/types'

/**
 * Who is on this bid — the roster card on the bid overview.
 *
 * Every member sees the list, whatever their own profile: knowing who else is on the deal is not a
 * privilege. Only an ADMIN sees the "+" and the per-row controls, because those are the only
 * actions the server will accept from them — the client check is an affordance, the server is the
 * gate (it answers 403 either way).
 *
 * The header mirrors the artefact explorer's: a search icon that widens into a field with a close
 * button, Escape folds it back.
 */
export function BidMembers({ bid }: { bid: string }) {
  const { t } = useLang()
  const qc = useQueryClient()
  const { isAdmin } = useBidProfile(bid)

  const [searching, setSearching] = useState(false)
  const [query, setQuery] = useState('')
  const [adding, setAdding] = useState(false)
  const [pick, setPick] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const members = useQuery({
    queryKey: ['members', bid],
    queryFn: () => api.bidMembers(bid),
    enabled: Boolean(bid),
  })

  const all = members.data?.members ?? []
  // Same substring approach as the explorer's filter: match the name OR the address.
  const needle = query.trim().toLowerCase()
  const shown = needle
    ? all.filter(
        (m) => m.displayName.toLowerCase().includes(needle) || m.email.includes(needle),
      )
    : all

  /** This codebase does not use useMutation — plain handlers + invalidateQueries. */
  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await action()
      await qc.invalidateQueries({ queryKey: ['members', bid] })
      // Someone's own access may have just changed, and `/v1/me` drives the whole profile-aware UI.
      await qc.invalidateQueries({ queryKey: ['me'] })
    } catch (e) {
      // The backend's refusals are the interesting ones — above all the last-admin guard, which is
      // a RULE and must read as one rather than as a breakage. Branch on the CODE, never on the
      // prose: the wording is the server's to change.
      if (isApiError(e) && e.code === 'last_admin') setError(t('members_last_admin'))
      else if (isApiError(e) && e.serverMessage) setError(e.serverMessage)
      else setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function closeSearch(): void {
    setSearching(false)
    setQuery('')
  }

  function closeAdd(): void {
    setAdding(false)
    setPick('')
    setError('')
  }

  return (
    <section className="members-card" aria-labelledby={`members-h-${bid}`}>
      <div className="members-head">
        {searching ? (
          <div className="members-search">
            <input
              type="search"
              autoFocus
              value={query}
              aria-label={t('members_search')}
              placeholder={t('members_search')}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeSearch()
              }}
            />
          </div>
        ) : (
          <>
            {/* A chip, not a heading — matching the "AI usage" / "Bid branding" titles on the two
                widgets it shares this page with, so all three read as one family. */}
            <span id={`members-h-${bid}`} className="home-eyebrow">{t('members_title')}</span>
            <span className="members-count">
              {t('members_count').replace('{n}', String(all.length))}
            </span>
          </>
        )}

        <span className="members-actions">
          <button
            type="button"
            className="members-iconbtn"
            aria-label={searching ? t('members_search_close') : t('members_search')}
            title={searching ? t('members_search_close') : t('members_search')}
            onClick={() => (searching ? closeSearch() : setSearching(true))}
          >
            {searching ? <IconClose /> : <IconSearch />}
          </button>

          {isAdmin && (
            <button
              type="button"
              className="members-iconbtn"
              aria-label={adding ? t('members_add_cancel') : t('members_add')}
              title={adding ? t('members_add_cancel') : t('members_add')}
              onClick={() => (adding ? closeAdd() : setAdding(true))}
            >
              {adding ? <IconClose /> : <IconPlus />}
            </button>
          )}
        </span>
      </div>

      {isAdmin && adding && (
        <div className="members-add">
          <PeoplePicker
            value={pick}
            autoFocus
            onChange={(email) => setPick(email)}
          />
          <button
            type="button"
            disabled={!pick || busy}
            onClick={() =>
              void run(async () => {
                // Least privilege by default: an admin promotes deliberately, from the row.
                await api.addBidMember(bid, pick, 'Contributor', 'viewer')
                closeAdd()
              })
            }
          >
            {t('members_confirm')}
          </button>
        </div>
      )}

      {error && (
        <p className="members-error" role="alert">
          {error}
        </p>
      )}

      {members.isLoading && <p className="members-empty">{t('loading')}</p>}

      {!members.isLoading && shown.length === 0 && (
        <p className="members-empty">{all.length === 0 ? t('members_none') : t('members_none_match')}</p>
      )}

      {shown.length > 0 && (
        <>
          {/* The column heads earn their place: `role` and `profile` are exactly the pair people
              conflate, and naming them is what stops "Bid manager / Admin" reading as one setting.
              aria-hidden because each control already carries its own per-row accessible name. */}
          <div className="members-cols" aria-hidden="true">
            <span>{t('members_col_member')}</span>
            <span>{t('members_col_profile')}</span>
            <span>{t('members_col_role')}</span>
            <span />
          </div>
          <ul className="members-list" aria-label={t('members_title')}>
            {shown.map((m) => (
              <MemberRow
                key={m.email}
                member={m}
                isAdmin={isAdmin}
                busy={busy}
                onChange={(patch) => void run(() => api.updateBidMember(bid, m.email, patch))}
                onRemove={() => void run(() => api.removeBidMember(bid, m.email))}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function MemberRow({
  member,
  isAdmin,
  busy,
  onChange,
  onRemove,
}: {
  member: BidMember
  isAdmin: boolean
  busy: boolean
  onChange: (patch: { role?: BidRole; profile?: Profile }) => void
  onRemove: () => void
}) {
  const { t } = useLang()
  const who = member.displayName || member.email

  return (
    <li className="members-row">
      {/* 1 — Member. The name owns the flexible column; "invited" sits UNDER it rather than
          beside it, so a long address never has to compete with a badge for width. */}
      <span className="members-who">
        <Avatar email={member.email} displayName={member.displayName} size={26} />
        <span className="members-who-text">
          <span className="members-name" title={member.email}>
            {who}
          </span>
          {/* Invited but never signed in: their Entra identity is not bound to the row yet. */}
          {!member.userId && <span className="members-invited">{t('members_invited')}</span>}
        </span>
      </span>

      {/* 2 — Profile: the ONLY axis that grants anything, so it leads. */}
      {isAdmin ? (
        <select
          className="members-select"
          aria-label={t('members_profile').replace('{who}', who)}
          title={t(`profile_${member.profile}` as Key)}
          value={member.profile}
          disabled={busy}
          onChange={(e) => onChange({ profile: e.target.value as Profile })}
        >
          {PROFILES.map((p) => (
            <option key={p} value={p}>
              {t(`profile_${p}` as Key)}
            </option>
          ))}
        </select>
      ) : (
        <span className="members-tag" title={t(`profile_${member.profile}` as Key)}>
          <span className="visually-hidden">{t('members_col_profile')}: </span>
          {t(`profile_${member.profile}` as Key)}
        </span>
      )}

      {/* 3 — Role: descriptive metadata, gates nothing. */}
      {isAdmin ? (
        <select
          className="members-select"
          aria-label={t('members_role').replace('{who}', who)}
          title={t(`role_${member.role}` as Key)}
          value={member.role}
          disabled={busy}
          onChange={(e) => onChange({ role: e.target.value as BidRole })}
        >
          {BID_ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`role_${r}` as Key)}
            </option>
          ))}
        </select>
      ) : (
        <span className="members-tag" title={t(`role_${member.role}` as Key)}>
          <span className="visually-hidden">{t('members_col_role')}: </span>
          {t(`role_${member.role}` as Key)}
        </span>
      )}

      {/* 4 — actions. Always rendered (empty for a non-admin) so every row keeps the same
          column count and the grid stays aligned. */}
      {isAdmin ? (
        <button
          type="button"
          className="members-iconbtn"
          aria-label={t('members_remove').replace('{who}', who)}
          title={t('members_remove').replace('{who}', who)}
          disabled={busy}
          onClick={onRemove}
        >
          <IconClose />
        </button>
      ) : (
        <span />
      )}
    </li>
  )
}
